import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';

export default function DetailImage({ identity, thumb, src, title, model, retry = 0, onOpen, onFailure, waiting = false }: {
    identity: string; thumb?: string; src?: string; title: string; model?: string | null; retry?: number;
    onOpen?: () => void; onFailure?: () => void; waiting?: boolean;
}) {
    const { t } = useContent();
    const [ready, setReady] = useState('');
    const [finished, setFinished] = useState('');
    const [failedThumb, setFailedThumb] = useState('');
    const [ratio, setRatio] = useState(3 / 4);
    const element = useRef<HTMLImageElement>(null);
    const current = useRef('');
    const key = `${identity}:${src || ''}:${retry}`;
    current.current = key;
    const generation = useRef(0);
    const failure = useRef(onFailure);
    failure.current = onFailure;
    const decode = useCallback((image: HTMLImageElement, version: number, expected: string) => {
        image.decode().then(() => {
            if (generation.current !== version || current.current !== expected) return;
            setRatio(image.naturalWidth / image.naturalHeight || 3 / 4);
            setReady(expected);
        }).catch(() => { if (generation.current === version && current.current === expected) failure.current?.(); });
    }, []);
    useEffect(() => {
        const version = ++generation.current;
        setFailedThumb('');
        const image = element.current;
        if (image?.complete && image.naturalWidth) decode(image, version, key);
        return () => { generation.current = version + 1; };
    }, [key, decode]);
    const visible = ready === key && !!src;
    useEffect(() => {
        if (!visible) return;
        const timer = window.setTimeout(() => setFinished(key), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260);
        return () => clearTimeout(timer);
    }, [visible, key]);
    return <button type="button" className="lab-detail-image lab-progressive-image relative block mx-auto mb-4 max-w-full"
        style={{ aspectRatio: ratio }} disabled={!visible || !onOpen} aria-label={t('detail.main', { title })} onClick={onOpen} data-image-ready={visible}>
        {finished !== key && thumb && failedThumb !== thumb && <img key={thumb} data-preview-image src={thumb} alt="" aria-hidden="true" className="lab-preview-blur"
            onLoad={event => { if (current.current === key && !src && event.currentTarget.naturalHeight) setRatio(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight); }} onError={() => { if (current.current === key) setFailedThumb(thumb); }} />}
        {src && <img key={key} ref={element} data-main-image src={src} alt={title} style={{ opacity: visible ? 1 : 0 }}
            onLoad={event => decode(event.currentTarget, generation.current, key)} onError={() => { if (current.current === key) failure.current?.(); }} />}
        {waiting && <span role="status" className="lab-image-wait">{t('imageDetail.loading')}</span>}
        {visible && model && <span className="lab-detail-model" title={t('detail.generatedWith', { model })}>{t('detail.generatedWith', { model })}</span>}
        {visible && onOpen && <span className="lab-detail-zoom" aria-hidden="true"><ZoomIn /></span>}
    </button>;
}
